import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import { App, Alert, Button, Col, Divider, Form, Row } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import isEqual from 'lodash/isEqual'

import Api from '@vacationtracker/shared/services/api'
import * as logger from '../../../../services/logger'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { useEffectivePlan } from '../../../../store/use-effective-plan'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'
import { useAppSelector } from '../../../../store/hooks'
import { getTeamById, getCreateTeamsData } from '../../../../graphql/custom-queries'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { PageHeader } from '../../../../design-system/PageHeader'
import { redirectIfEntityNotFound } from '../../../../util/entity-not-found-redirect'
import CircularProgress from '../../../../components/circular-progress'
import TeamForm from '../../../../components/team-form'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { getDescendantTeamIds } from '@vacationtracker/shared/functions/team-tree'
import { MAX_DEPTH_FOR_NESTED_DEPARMENTS_COMPLETE, MAX_DEPTH_FOR_NESTED_DEPARMENTS_CORE, NUMBER_OF_TEAMS_LIMIT } from '@vacationtracker/shared/data/app-parameters'
import { useLocationAdmin } from '../../../../hooks/use-location-admin'

import { IGetTeamByIdData, IGetCreateTeamsData, IGetUserListShort, ITeamEventV2, IEditTeamById, ITeamApproversWithLevel, ITeamUsers } from '../../../../types/teams'
import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { ApproversLevelEnum, ITeamShort } from '@vacationtracker/shared/types/team'
import { AddonTypeEnum } from '@vacationtracker/shared/types/addons'
import { clearWizardState } from '../../../../util/onboarding-wizard-storage'

interface ITeamsFormPage {
  match: {
    params: {
      id: string
    }
  }
}

// Depth of every team (root = 1) via parentId chains. Memoized per team and
// visited-set guarded so it terminates even on data that already contains a
// parentId cycle.
const computeTeamDepths = (teamList: ITeamShort[]): Map<string, number> => {
  const byId = new Map(teamList.map((team) => [team.id, team]))
  const depths = new Map<string, number>()

  teamList.forEach((team) => {
    const chain: string[] = []
    const visited = new Set<string>()
    let current: ITeamShort | undefined = team
    while (current && !depths.has(current.id) && !visited.has(current.id)) {
      visited.add(current.id)
      chain.push(current.id)
      current = current.parentId ? byId.get(current.parentId) : undefined
    }
    let depth = current && depths.has(current.id) ? (depths.get(current.id) as number) : 0
    for (let i = chain.length - 1; i >= 0; i--) {
      depth += 1
      depths.set(chain[i], depth)
    }
  })

  return depths
}

const TeamsFormPage = ({ match }: ITeamsFormPage): React.ReactElement => {
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const history = useHistory()
  const { notification } = App.useApp()

  const [team, setTeam] = useState<IEditTeamById>({
    id: '',
    name: '',
    timestamp: '',
    users: [],
    approverIdsFirstLevel: [],
    approverIdsSecoundLevel: [],
    approvalLevel: ApproversLevelEnum.FIRST,
    isDefault: false,
    parentId: null,
    keepLeaveRequestsOpen: false,
  })
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [isTeamLoading, setTeamLoading] = useState(true)
  const [users, setUsers] = useState<IGetUserListShort[]>([])
  const [isLoading, setIsloading] = useState(true)
  const [totalTeams, setTotalTeam] = useState<number>(1)
  const [submitLoader, setSubmitLoader] = useState(false)
  const [correlationId, setCorrelationId] = useState('')
  const [correlationExist, setCorrelationExist] = useState(false)
  const [allowUnlimitedTeams, setAllowUnlimitedTeams] = useState(false)
  const [teamUserObjects, setTeamUserObjects] = useState<ITeamUsers[]>([])
  const [teamApproverObjects, setTeamApproverObjects] = useState<ITeamUsers[]>([])

  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)

  const { isLocationAdmin } = useLocationAdmin()

  const [getCreateTeamsDataQuery] = useManualQuery<IGetCreateTeamsData>(getCreateTeamsData)
  const [getTeamByIdQuery] = useManualQuery<IGetTeamByIdData, { id: string }>(getTeamById)

  useEffect(() => {
    if (match.params.id) {
      fetchTeam(match.params.id)
    }
    fetchTotalTeamsAndUsers()
  }, [match.params.id])

  useEffect(() => {
    if (!correlationExist) {
      const collerationStatus = actionNotifications.includes(correlationId)
      setCorrelationExist(collerationStatus)
      setSubmitLoader(collerationStatus)
    }
    if (correlationExist && actionNotifications && !actionNotifications.includes(correlationId)) {
      setCorrelationId('')
      backToTeams()
    }
  }, [actionNotifications, correlationExist, correlationId])

  const visibleUserIdSet = useMemo(() => new Set(users.map((u) => u.id)), [users])

  const hiddenUsers = useMemo(() => {
    if (!isLocationAdmin || users.length === 0) return []
    const allTeamPeople = [...teamUserObjects, ...teamApproverObjects]
    return allTeamPeople.filter((u) => !visibleUserIdSet.has(u.id)).filter((u, i, arr) => arr.findIndex((x) => x.id === u.id) === i)
  }, [isLocationAdmin, users, teamUserObjects, teamApproverObjects, visibleUserIdSet])

  const formInitialValues = useMemo(() => {
    if (!isLocationAdmin || !team.id) return team
    return {
      ...team,
      users: team.users.filter((id) => visibleUserIdSet.has(id)),
      approverIdsFirstLevel: team.approverIdsFirstLevel.filter((id) => visibleUserIdSet.has(id)),
      approverIdsSecoundLevel: team.approverIdsSecoundLevel.filter((id) => visibleUserIdSet.has(id)),
    }
  }, [team, visibleUserIdSet, isLocationAdmin])

  useEffect(() => {
    if (authCompany?.plan === SubscriptionPlanEnum.freemium && !match.params.id) {
      return history.push(FrontendUrls.departments)
    }
  }, [authCompany?.plan])

  // Which teams may be offered as a parent:
  // - not the edited team's own subtree (would create a parentId cycle), and
  // - deep enough that the edited team's whole subtree still fits within the
  //   plan's depth limit (mirrors validateSubdepartmentsLimit on the backend).
  // The current parent is always kept so the form can render legacy structures
  // that already exceed the limit.
  const parentTeamOptions = useMemo(() => {
    const maxDepth =
      effectivePlan === SubscriptionPlanEnum.core
        ? MAX_DEPTH_FOR_NESTED_DEPARMENTS_CORE
        : effectivePlan === SubscriptionPlanEnum.freemium
          ? // Freemium allows no subdepartments at all - only the root level exists.
            1
          : MAX_DEPTH_FOR_NESTED_DEPARMENTS_COMPLETE
    const depths = computeTeamDepths(teams)

    const descendantIds = match.params.id ? getDescendantTeamIds(teams, match.params.id) : new Set<string>()
    let subtreeHeight = 1
    if (match.params.id) {
      const ownDepth = depths.get(match.params.id) ?? 1
      descendantIds.forEach((descendantId) => {
        const relativeHeight = (depths.get(descendantId) ?? ownDepth) - ownDepth + 1
        if (relativeHeight > subtreeHeight) {
          subtreeHeight = relativeHeight
        }
      })
    }

    return teams.filter((candidate) => !descendantIds.has(candidate.id) && ((depths.get(candidate.id) ?? 1) + subtreeHeight <= maxDepth || candidate.id === team.parentId))
  }, [teams, match.params.id, team.parentId, effectivePlan])

  const fetchTotalTeamsAndUsers = async () => {
    try {
      const response = await getCreateTeamsDataQuery()
      if (!response.data || response.error) throw response.error

      setTeams(response.data.getTeamList)
      setTotalTeam(response.data.getTeamList.length)
      setUsers(response.data.getUsersFiltered.users)
      if (response.data?.getAddonsSubscription) {
        setAllowUnlimitedTeams(() => {
          return response.data?.getAddonsSubscription?.addons.find((addon) => addon.type === AddonTypeEnum.NO_LIMIT_TEAMS)?.status === 'ACTIVE'
        })
      }
      setIsloading(false)
    } catch (err) {
      logger.error('error fetching total teams and users', err)
    }
  }

  const fetchTeam = async (teamId: string) => {
    try {
      const response = await getTeamByIdQuery({ variables: { id: teamId } })
      if (!response.data || response.error) throw response.error
      const team = response.data.getTeam
      if (!redirectIfEntityNotFound(team, FrontendUrls.departments, history)) return
      setTeamUserObjects(team.users)
      setTeamApproverObjects(team.approvers)
      const users = team.users.map(({ id }) => id)
      const approvers: ITeamApproversWithLevel[] =
        team.approvers && typeof team.approvers[0] === 'string'
          ? team.approvers.map(({ id }) => {
              return {
                userId: id,
                approverLevels: [ApproversLevelEnum.FIRST],
              }
            })
          : team.approvers.map((approver) => {
              return {
                userId: approver.id,
                approverLevels: approver.approverLevels as ApproversLevelEnum[],
              }
            })

      setTeam({
        id: team.id,
        name: team.name,
        timestamp: team.timestamp,
        isDefault: team.isDefault,
        parentId: team.parentId,
        users,
        approverIdsFirstLevel: approvers.filter((approver) => approver.approverLevels.includes(ApproversLevelEnum.FIRST)).map((approver) => approver.userId),
        approverIdsSecoundLevel: approvers.filter((approver) => approver.approverLevels.includes(ApproversLevelEnum.SECOND)).map((approver) => approver.userId),
        approvalLevel: approvers.find((approver) => approver.approverLevels.includes(ApproversLevelEnum.SECOND)) ? ApproversLevelEnum.SECOND : ApproversLevelEnum.FIRST,
        watchersToNotify: team.watchersToNotify,
        keepLeaveRequestsOpen: Boolean(team.keepLeaveRequestsOpen),
      })
      form.resetFields()
      setTeamLoading(false)
    } catch (err) {
      logger.error('error fetching team', err)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorNotification = (error: any, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'notifications.handleSubmitError' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.notificationGeneral' }),
      description,
      duration: 0,
    })
  }

  const onFinish = async () => {
    let response
    try {
      const values = await form.validateFields()

      if (isLocationAdmin && team.id) {
        const hiddenUserIds = team.users.filter((id) => !visibleUserIdSet.has(id))
        const hiddenApproverIdsFirst = team.approverIdsFirstLevel.filter((id) => !visibleUserIdSet.has(id))
        const hiddenApproverIdsSecond = team.approverIdsSecoundLevel.filter((id) => !visibleUserIdSet.has(id))
        values.users = [...(values.users || []), ...hiddenUserIds]
        values.approverIdsFirstLevel = [...(values.approverIdsFirstLevel || []), ...hiddenApproverIdsFirst]
        values.approverIdsSecoundLevel = [...(values.approverIdsSecoundLevel || []), ...hiddenApproverIdsSecond]
      }

      setSubmitLoader(true)
      const correlationIds: string[] = []

      if (
        team.name !== values.name ||
        !isEqual(team.approverIdsFirstLevel, values.approverIdsFirstLevel) ||
        !isEqual(team.approverIdsSecoundLevel, values.approverIdsSecoundLevel) ||
        !isEqual(team.users, values.users) ||
        !isEqual(team.isDefault, values.isDefault) ||
        !isEqual(team.parentId, values.parentId) ||
        !isEqual(team.watchersToNotify, values.watchersToNotify) ||
        !isEqual(team.keepLeaveRequestsOpen, values.keepLeaveRequestsOpen)
      ) {
        const approvers = values.approverIdsFirstLevel.map((approverId) => {
          return {
            userId: approverId,
            approverLevels: [ApproversLevelEnum.FIRST],
          }
        })

        if (values?.approverIdsSecoundLevel) {
          values.approverIdsSecoundLevel.forEach((approverId) => {
            if (approvers.find(({ userId }) => userId === approverId)) {
              approvers.find(({ userId }) => userId === approverId).approverLevels.push(ApproversLevelEnum.SECOND)
            } else {
              approvers.push({
                userId: approverId,
                approverLevels: [ApproversLevelEnum.SECOND],
              })
            }
          })
        }

        const body: ITeamEventV2 = {
          eventType: match.params.id ? 'TEAM_UPDATED' : 'TEAM_CREATED',
          eventGroup: 'TEAM',
          name: values.name,
          approvers: approvers,
          isDefault: values.isDefault,
          users: values.users,
          keepLeaveRequestsOpen: Boolean(values.keepLeaveRequestsOpen),
          ...(values.parentId && { parentId: values.parentId }),
          ...(values.watchersToNotify && { watchersToNotify: values.watchersToNotify }),
          version: 2,
        }

        if (match.params.id) {
          body.teamId = match.params.id
        }

        response = await Api.post('/core/event', body)

        clearWizardState(authUser.id, authCompany?.id)
        const correlationId: string = response.correlationId

        notification.open({
          key: correlationId,
          message: match.params.id
            ? formatMessage({ id: 'team.updateInProgress' }, { teamName: response.name })
            : formatMessage({ id: 'team.createInProgress' }, { teamName: response.name }),
          icon: <LoadingOutlined />,
          duration: 0,
        })

        correlationIds.push(correlationId)
        setCorrelationId(correlationId)
      }

      setActionNotifications([...actionNotifications, ...correlationIds])
    } catch (error) {
      if (response?.correlationId) {
        handleErrorNotification(error, response.correlationId as string)
      }
      logger.error(error)
      setSubmitLoader(false)
      handleErrorNotification(error, undefined, formatMessage({ id: 'error.somethingWentWrong' }))
    }
  }

  const backToTeams = () => {
    history.push(FrontendUrls.departments)
  }

  const shouldDisableCreatingNewTeam = (companyFeatureFlags, companyPlan, teamCount, numberOfTeamsLimit, allowUnlimited) => {
    if (companyFeatureFlags.includes(FeatureFlagEnum.noLimitTeams)) {
      return false
    }

    if (companyPlan === SubscriptionPlanEnum.freemium) return true

    return companyPlan === SubscriptionPlanEnum.core && teamCount >= numberOfTeamsLimit && !allowUnlimited
  }

  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  }

  return (
    <div className="main-content form-background">
      {(match.params.id && isTeamLoading) || isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <PageHeader
            title={team.id ? team.name : <IntlMessages id="teamForm.createTeam" />}
            parent={[{ label: <IntlMessages id="app.departments" />, to: FrontendUrls.departments }]}
          />
          <div className="main-content-body">
            <div className="form-layout">
              {!team.id && shouldDisableCreatingNewTeam(featureFlags, effectivePlan, totalTeams, NUMBER_OF_TEAMS_LIMIT, allowUnlimitedTeams) && (
                <Alert
                  style={{ marginBottom: 20 }}
                  message={
                    <IntlMessages
                      id="teamForm.teamLimitReached"
                      values={{ limitNumber: NUMBER_OF_TEAMS_LIMIT, billingPage: (...chunks) => <Link to="/app/settings/billing">{chunks}</Link> }}
                    />
                  }
                  type="warning"
                />
              )}
              <Form
                {...layout}
                form={form}
                layout="vertical"
                name="teamForm"
                initialValues={formInitialValues}
                className="team-from"
                onFinish={() => {
                  onFinish()
                }}
                disabled={!team.id && shouldDisableCreatingNewTeam(featureFlags, effectivePlan, totalTeams, NUMBER_OF_TEAMS_LIMIT, allowUnlimitedTeams)}
                size="large"
                requiredMark="optional"
                scrollToFirstError
              >
                <TeamForm
                  team={team}
                  teams={parentTeamOptions}
                  totalTeams={totalTeams}
                  users={users}
                  formType={match.params.id ? 'update' : 'create'}
                  plan={effectivePlan as SubscriptionPlan}
                  allowMultiLevelApproval={Boolean(featureFlags.includes(FeatureFlagEnum.multiLevelApproval))}
                  form={form}
                  disableAll={!team.id && shouldDisableCreatingNewTeam(featureFlags, effectivePlan, totalTeams, NUMBER_OF_TEAMS_LIMIT, allowUnlimitedTeams)}
                  isLocationAdmin={isLocationAdmin}
                  hiddenUsers={hiddenUsers}
                />

                <Divider />

                <Row
                  gutter={{
                    xs: 8,
                    sm: 16,
                    md: 24,
                    lg: 32,
                  }}
                >
                  <Col span={24}>
                    <Form.Item style={{ textAlign: 'right' }}>
                      <Button className="button-right" type="default" onClick={backToTeams}>
                        <IntlMessages id="app.cancel" />
                      </Button>
                      <Button className="button-right" htmlType="submit" type="primary" loading={submitLoader} data-qa="submit-new-department">
                        {team.id ? <IntlMessages id="teamForm.updateTeam" /> : <IntlMessages id="teamForm.createTeam" />}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TeamsFormPage
